import { ModuleId, QuestionState, DifficultyLevel } from '../types'
import { ALL_QUESTIONS_CORE } from '../data/questionLoader'
import { getLevelById } from '../config/levels'
import { GroupId } from '../config/systemConfig'

/**
 * Knowledge State Engine v7.0
 *
 * Progressive mastery model:
 * - unseen: Never attempted
 * - learning: Attempted but not yet consistent
 * - practiced: Getting consistent (2+ correct in recent window)
 * - mastered: Demonstrated reliable knowledge (3+ consecutive correct)
 *
 * Key principles:
 * - Mastery requires CONSISTENCY, not a single correct answer
 * - Wrong answers reduce confidence but don't reset completely
 * - Group-aware calculations for proper context isolation
 */

// Mastery configuration
const MASTERY_CONFIG = {
  /** Consecutive correct answers needed for 'mastered' status */
  CONSECUTIVE_FOR_MASTERY: 3,
  /** Consecutive correct answers needed for 'practiced' status */
  CONSECUTIVE_FOR_PRACTICED: 2,
  /** Minimum total attempts before mastery is possible */
  MIN_ATTEMPTS_FOR_MASTERY: 3,
  /** How many recent attempts to consider for status calculation */
  HISTORY_WINDOW: 8,
  /** Mastery points awarded per consecutive correct answer */
  POINTS_PER_CORRECT: 0.35,
  /** Mastery points lost per wrong answer */
  POINTS_PER_WRONG: -0.25,
  /** Minimum mastery points for 'mastered' status */
  MASTERY_POINT_THRESHOLD: 0.85,
  /** Minimum mastery points for 'practiced' status */
  PRACTICED_POINT_THRESHOLD: 0.5,
}

/**
 * Calculate consecutive correct answers from the end of history
 */
function getConsecutiveCorrectFromEnd(
  history: { isCorrect: boolean; timestamp: number }[],
): number {
  let count = 0
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].isCorrect) {
      count++
    } else {
      break
    }
  }
  return count
}

/**
 * Calculate mastery points from history using a weighted approach
 * Recent attempts count more than older ones
 */
function calculateMasteryPoints(
  history: { isCorrect: boolean; timestamp: number }[],
): number {
  if (history.length === 0) return 0

  const window = history.slice(-MASTERY_CONFIG.HISTORY_WINDOW)
  let points = 0

  window.forEach((attempt, index) => {
    // Weight increases for more recent attempts (index 0 = oldest in window)
    const recencyWeight = (index + 1) / window.length
    if (attempt.isCorrect) {
      points += MASTERY_CONFIG.POINTS_PER_CORRECT * recencyWeight
    } else {
      points += MASTERY_CONFIG.POINTS_PER_WRONG * recencyWeight
    }
  })

  // Normalize to 0-1 range
  return Math.max(0, Math.min(1, points))
}

/**
 * Determine question status based on history
 */
function determineStatus(
  history: { isCorrect: boolean; timestamp: number }[],
  masteryPoints: number,
  totalAttempts: number,
): 'unseen' | 'learning' | 'practiced' | 'mastered' {
  if (totalAttempts === 0) return 'unseen'

  const consecutiveCorrect = getConsecutiveCorrectFromEnd(history)

  // Mastered: enough consecutive correct AND enough total attempts AND high mastery points
  if (
    consecutiveCorrect >= MASTERY_CONFIG.CONSECUTIVE_FOR_MASTERY &&
    totalAttempts >= MASTERY_CONFIG.MIN_ATTEMPTS_FOR_MASTERY &&
    masteryPoints >= MASTERY_CONFIG.MASTERY_POINT_THRESHOLD
  ) {
    return 'mastered'
  }

  // Practiced: some consistency shown
  if (
    consecutiveCorrect >= MASTERY_CONFIG.CONSECUTIVE_FOR_PRACTICED &&
    masteryPoints >= MASTERY_CONFIG.PRACTICED_POINT_THRESHOLD
  ) {
    return 'practiced'
  }

  // Learning: has been attempted but not yet consistent
  return 'learning'
}

/**
 * Update question state with progressive mastery model
 */
export function updateQuestionState(
  currentState: QuestionState | undefined,
  questionId: string,
  isCorrect: boolean,
  difficulty?: DifficultyLevel,
): QuestionState {
  const now = Date.now()

  // Initialize if new question
  if (!currentState) {
    currentState = {
      questionId,
      status: 'unseen',
      attempts: 0,
      correctAttempts: 0,
      lastAttemptDate: 0,
      masteryPoints: 0,
      history: [],
    }
  }

  // Update history (keep last HISTORY_WINDOW entries)
  const newHistory = [
    ...currentState.history,
    { isCorrect, timestamp: now },
  ].slice(-MASTERY_CONFIG.HISTORY_WINDOW)

  const newAttempts = currentState.attempts + 1
  const newCorrectAttempts = currentState.correctAttempts + (isCorrect ? 1 : 0)

  // Calculate mastery points from full history
  const masteryPoints = calculateMasteryPoints(newHistory)

  // Determine new status
  const newStatus = determineStatus(newHistory, masteryPoints, newAttempts)

  return {
    questionId,
    status: newStatus,
    attempts: newAttempts,
    correctAttempts: newCorrectAttempts,
    lastAttemptDate: now,
    masteryPoints,
    history: newHistory,
  }
}

/**
 * Calculate mastery percentage for a module, optionally filtered by group and/or difficulty
 *
 * CRITICAL FIX: Now supports group filtering to ensure proper context isolation
 */
export function calculateMasteryPercentage(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  difficulty?: DifficultyLevel,
  group?: GroupId,
): number {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId) return false
    if (difficulty !== undefined && q.difficulty !== difficulty) return false

    // Group filtering: only include questions belonging to this group
    if (group !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level || level.group !== group) return false
    }

    return true
  })

  const totalQuestions = moduleQuestions.length
  if (totalQuestions === 0) return 0

  // Use mastery points for a more nuanced percentage
  // 'mastered' = 1.0, 'practiced' = 0.6, 'learning' = 0.2, 'unseen' = 0
  let totalScore = 0
  moduleQuestions.forEach((q) => {
    const state = questionStates[q.id]
    if (!state || state.status === 'unseen') {
      totalScore += 0
    } else if (state.status === 'mastered') {
      totalScore += 1.0
    } else if (state.status === 'practiced') {
      totalScore += 0.6
    } else if (state.status === 'learning') {
      totalScore += 0.2
    }
  })

  return Math.round((totalScore / totalQuestions) * 100)
}

/**
 * Get review queue - questions that need more practice
 * Returns questions in 'learning' status (previously wrong, not yet consistent)
 */
export function getReviewQueue(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
): string[] {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId) return false
    if (group !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level || level.group !== group) return false
    }
    return true
  })

  return moduleQuestions
    .filter((q) => {
      const state = questionStates[q.id]
      return state?.status === 'learning'
    })
    .map((q) => q.id)
}

/**
 * Get questions by status for a module (group-aware)
 */
export function getQuestionsByStatus(
  moduleId: ModuleId,
  status: 'unseen' | 'learning' | 'practiced' | 'mastered',
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
): string[] {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId) return false
    if (group !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level || level.group !== group) return false
    }
    return true
  })

  return moduleQuestions
    .filter((q) => {
      const state = questionStates[q.id]
      if (status === 'unseen') {
        return !state || state.status === 'unseen'
      }
      return state?.status === status
    })
    .map((q) => q.id)
}

/**
 * Get module statistics (group-aware)
 */
export function getModuleStats(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
) {
  const moduleQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId) return false
    if (group !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level || level.group !== group) return false
    }
    return true
  })

  const stats = {
    total: moduleQuestions.length,
    unseen: 0,
    learning: 0,
    practiced: 0,
    mastered: 0,
    reviewNeeded: 0,
  }

  moduleQuestions.forEach((q) => {
    const state = questionStates[q.id]
    if (!state || state.status === 'unseen') {
      stats.unseen++
    } else if (state.status === 'mastered') {
      stats.mastered++
    } else if (state.status === 'practiced') {
      stats.practiced++
    } else if (state.status === 'learning') {
      stats.learning++
      stats.reviewNeeded++
    }
  })

  return stats
}
